'use client'

import { Post } from '@/payload-types'
import { Locale } from '@/providers/Locale/types'
import { JSX, MouseEventHandler, useCallback, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

import { slugToLink } from '@/utilities/slugToLink'
import { cn } from '@/utilities/ui'
import { useHeaderVariant } from '@/providers/HeaderVariant'
import { useTheme } from '@/providers/Theme'

import { AYAdSlot } from '@/components/frontend/AYAdSlot'
import RichText from '@/components/payload/RichText'
import { MediaWithFallback } from '@/components/frontend/MediaWithFallback'
import { InfiniteScrollPosts } from '@/components/frontend/InfiniteScrollPosts'
import { EmbeddedButton } from '@/components/frontend/Slideshow/EmbeddedButton'
import { SlideshowFooter } from '@/components/frontend/Slideshow/SlideshowFooter'
import { SlideshowHeader } from '@/components/frontend/Slideshow/SlideshowHeader'
import { SidebarAds } from '@/components/frontend/SidebarAds'
import { ByAuthor } from '@/components/frontend/ByAuthor'
import { FlatTitle, type ChildBlock } from '@/components/frontend/FlatTitle'
import { PublishedDateInfo } from '@/components/frontend/PublishedDateInfo'
import { CategoryAccentLink } from '@/components/frontend/CategoryAccentLink'
import { sendGA4Event } from '@/providers/GoogleTagManager'

interface Props {
    post: Post
    locale: Locale
    params?: {
        slide: number
    }
}

export function Slideshow({ post, params, locale }: Props): JSX.Element {
    const initialSlide = params?.slide || 0
    const [page, setPage] = useState(initialSlide > 0 ? initialSlide - 1 : 0)
    const { setHeaderVariant } = useHeaderVariant()
    const { setTheme } = useTheme()
    const postUrl = slugToLink(post.slug)
    const pathname = usePathname()

    const { categories, heroImage, populatedAuthors, publishedAt, updatedAt, title, meta } = post
    const category = categories?.[categories.length - 1]
    const categoryTitle = typeof category === 'object' ? category.title : category || 'Untitled'
    const categorySlug = typeof category === 'object' ? category.slug : category || 'untitled'
    const headings: Array<ChildBlock | string> = [title]
    const slideNumber = page + 1
    const segments = post.content.root.children
        .reduce<Array<Array<ChildBlock>>>(
            (groups, child) => {
                if (child.type === 'heading') {
                    headings.push(child)
                    groups.push([])
                } else {
                    groups.slice(-1)[0]?.push(child)
                }

                return groups
            },
            [[]],
        )

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((group) => ({ root: { ...post.content.root, children: group as any[] } }))

    const parseSlideNumber = useCallback(
        (mPathname: string) => {
            // back/forward button was pressed, restore state from url
            const pathParts = mPathname.split('/')
            if (pathParts.slice(-2)[0] === 'slides') {
                const urlSlideNumber = Number(pathParts.slice(-1)[0]) || 1

                setPage(Math.max(urlSlideNumber - 1, 0))
            } else {
                setPage(0)
            }
        },
        [setPage],
    )

    const sendSlideShowEvent = useCallback(
        (event: string, slideNumber: number) => {
            sendGA4Event(event, {
                content_type: 'slideshow',
                content_title: post.title,
                content_id: post.id,
                slideshow_page_number: slideNumber,
                slideshow_length: segments.length,
                content_author: populatedAuthors?.[0]?.name || 'Unknown Author',
                content_created_date: publishedAt || '',
                content_updated_date: updatedAt,
            })
        },
        [post.title, post.id, segments.length, populatedAuthors, publishedAt, updatedAt],
    )

    const onNavigationClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (e) => {
            let newPage = 0
            const isRestart = e.currentTarget.dataset['direction'] === 'restart'
            if (isRestart) {
                newPage = 0
            } else {
                const direction = e.currentTarget.dataset['direction'] === 'prev' ? -1 : 1

                // Set max-min to prevent spammed clicks from changing the page count too far
                newPage = Math.min(Math.max(page + direction, 0), segments.length)
            }

            const url = newPage > 0 ? `${postUrl}/slides/${newPage + 1}` : postUrl
            window.history.pushState({ slide: newPage + 1 }, '', url)
            setPage(newPage)

            // For accessibility
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' })

            if (newPage === 0 || isRestart) {
                sendSlideShowEvent('slideshow_begin', newPage + 1)
            } else if (newPage === segments.length) {
                sendSlideShowEvent('slideshow_complete', newPage + 1)
            } else {
                sendSlideShowEvent('slideshow_progress', newPage + 1)
            }
        },
        [postUrl, segments.length, page, sendSlideShowEvent],
    )

    // Client side navigation parsing of pathname
    // This can happen when the slide list is clicked
    useEffect(() => {
        parseSlideNumber(pathname)
    }, [parseSlideNumber, pathname])

    useEffect(() => {
        // If this is slideshow dark, then we use the dark theme
        if (post?.tags?.some((tag) => typeof tag !== 'string' && tag.slug && tag.slug === 'slideshowdark')) {
            setTheme('dark')
        }

        // Reset theme on page change
        return () => {
            setTheme('light')
        }
    }, [setTheme, post])

    // Set up header menu style
    useEffect(() => {
        setHeaderVariant({ theme: 'dark', variant: 'thin', title: post.title })

        // Reset the header on page change
        return () => {
            setHeaderVariant({ theme: 'light', variant: 'standard' })
        }
    }, [post, setHeaderVariant])

    // Listen on window for bfcache since we can't shallow route with App router
    useEffect(() => {
        const parseSlideNumberFromWindow = () => parseSlideNumber(window.location.pathname)
        window.addEventListener('popstate', parseSlideNumberFromWindow)
        return () => {
            window.removeEventListener('popstate', parseSlideNumberFromWindow)
        }
    }, [parseSlideNumber])

    return (
        <>
            <div className='container flex flex-row gap-8 pt-8 pb-10 mx-auto'>
                <div className='max-w-[856px]'>
                    <SlideshowHeader
                        locale={locale}
                        postUrl={postUrl}
                        headings={headings}
                        slideNumber={slideNumber}
                        totalSlides={segments.length}
                        onNavigationClick={onNavigationClick}
                    />
                    <section data-index={1} className={cn('border-b border-[#E5E5E5] lg:border-0 mb-8 lg:mb-0', { 'lg:hidden': page !== 0 })}>
                        <div>
                            <CategoryAccentLink slug={categorySlug} title={categoryTitle} className='text-sm mb-6' />
                            <div>
                                <h1 className='mb-4 md:mb-6 text-2xl md:text-5xl font-bold'>
                                    <FlatTitle heading={headings[0]} />
                                </h1>
                                <h2 className='mb-4 text-lg md:text-3xl leading-5 md:leading-8'>{meta?.description}</h2>
                            </div>
                            <div className='payload-richtext prose md:prose-md dark:prose-invert max-w-[48rem] flex flex-row gap-4 text-base whitespace-nowrap mb-4'>
                                <ByAuthor authors={populatedAuthors} postId={post.id} styles={{ by: 'text-black/70' }} />
                                <PublishedDateInfo publishedAt={publishedAt} updatedAt={updatedAt} locale={locale} />
                            </div>
                        </div>
                        <div className='select-none relative mb-8'>
                            <MediaWithFallback media={heroImage} options={{ className: 'w-full', width: 780, height: 520 }} />
                            <EmbeddedButton
                                variant='start'
                                locale={locale}
                                onClick={onNavigationClick}
                                className='absolute top-[40px] right-0'
                                data-direction='next'
                            />
                            <div className='lg:hidden my-4'>
                                <AYAdSlot adId='theinventory.com_336x280' width={336} height={280} />
                            </div>
                        </div>
                        {segments.length > 0 && (
                            <>
                                <RichText className='max-w-[48rem] mx-auto lg:text-xl lg:[&>h2]:text-3xl pb-8' data={segments[0]!} enableGutter={false} />
                                <div className='lg:hidden my-4'>
                                    <AYAdSlot adId='theinventory.com_336x280' width={336} height={280} />
                                </div>
                            </>
                        )}
                        <div className='text-center'>
                            <EmbeddedButton variant='start' locale={locale} onClick={onNavigationClick} data-direction='next' />
                        </div>
                    </section>
                    {segments.slice(1).map((segment, index) => (
                        <section
                            key={index + 1}
                            data-index={index + 1}
                            className={cn('relative group border-b border-[#E5E5E5] lg:border-0 mb-8 lg:mb-0', { 'lg:hidden': index + 1 !== page })}
                        >
                            <p className='text-lg lg:hidden mb-4'>
                                {index + 2} / {segments.length}
                            </p>
                            <h2 className='border-b-2 border-[#E5E5E5] font-bold text-lg pb-1 lg:hidden inline-block'>
                                <FlatTitle heading={headings[index + 1]} />
                            </h2>
                            <RichText data={segment} enableGutter={false} />
                            <div className='lg:hidden my-4'>
                                <AYAdSlot adId='theinventory.com_336x280' width={336} height={280} />
                            </div>
                            <EmbeddedButton
                                variant='next'
                                locale={locale}
                                onClick={onNavigationClick}
                                className='top-[102px] right-0 absolute opacity-0'
                                data-direction='next'
                            />
                            <EmbeddedButton
                                variant='previous'
                                locale={locale}
                                onClick={onNavigationClick}
                                className='top-[102px] left-0 absolute opacity-0'
                                data-direction='prev'
                            />
                        </section>
                    ))}
                    <div className={cn({ hidden: page < segments.length })}>
                        <InfiniteScrollPosts />
                    </div>
                </div>

                <aside className='hidden lg:block w-full lg:w-[1/3] max-w-[448px] min-w-[336px] px-4'>
                    <SidebarAds adCount={2} />
                </aside>
            </div>
            <SlideshowFooter locale={locale} post={post} slideNumber={slideNumber} totalSlides={segments.length} onNavigationClick={onNavigationClick} />
        </>
    )
}
